import { Columns3, Maximize2, Minimize2, Rows3, SquareStack } from 'lucide-react';
import { cn } from '../../lib/utils.ts';
import { Button } from '../ui/button.tsx';

export type ListLayout = 'table' | 'board' | 'multiboard';

export type BoardDensity = 'normal' | 'compact';

/** Normal or Compact columns (half as wide) for every board of the list. */
export function DensityToggle({
  density,
  onChange,
}: {
  density: BoardDensity;
  onChange: (next: BoardDensity) => void;
}) {
  const option = (value: BoardDensity, label: string, Icon: typeof Rows3) => (
    <Button
      variant="ghost"
      size="sm"
      className={cn('h-7', density === value && 'bg-card shadow-card hover:bg-card')}
      aria-pressed={density === value}
      onClick={() => onChange(value)}
    >
      <Icon /> {label}
    </Button>
  );
  return (
    <div className="bg-muted flex gap-0.5 rounded-lg p-0.5" role="group" aria-label="Density">
      {option('normal', 'Normal', Maximize2)}
      {option('compact', 'Compact', Minimize2)}
    </div>
  );
}

/**
 * Table or board: the same list, the same view state (RELAY-1038). `multi` adds Multi board,
 * a board per pipeline stacked (Projects, whose records sit in several pipelines at once).
 * `tableLabel` names the non-board layout: `List` on the task pages (TS-1742, TS-1744).
 */
export function LayoutToggle({
  layout,
  onChange,
  multi = false,
  tableLabel = 'Table',
}: {
  layout: ListLayout;
  onChange: (next: ListLayout) => void;
  multi?: boolean;
  tableLabel?: string;
}) {
  const option = (value: ListLayout, label: string, Icon: typeof Rows3) => (
    <Button
      variant="ghost"
      size="sm"
      className={cn('h-7', layout === value && 'bg-card shadow-card hover:bg-card')}
      aria-pressed={layout === value}
      onClick={() => onChange(value)}
    >
      <Icon /> {label}
    </Button>
  );
  return (
    <div className="bg-muted flex gap-0.5 rounded-lg p-0.5" role="group" aria-label="Layout">
      {option('table', tableLabel, Rows3)}
      {option('board', 'Board', Columns3)}
      {multi && option('multiboard', 'Multi board', SquareStack)}
    </div>
  );
}
